import {
  Article,
  ArticleAction,
  ArticleFooter,
  ArticleImage,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { useAddLineItem } from "@babackapp/return-widget-components/hooks/use-add-line-item";
import type { OrderLineItem } from "@babackapp/return-widget-components/types";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { parseLineItemPriceInCents } from "@babackapp/return-widget-components/utils/get-line-item-price";
import { ChevronRight, InfoIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatVariantOptions } from "../../utils/format-variant-options";

type LineItemRowProps = {
  lineItem: OrderLineItem;
  orderId: string;
  shopId: string;
  currency?: string;
  isConnectedUser?: boolean;
  connectedUserTooltip?: string;
};

export function LineItemRow({
  lineItem,
  orderId,
  shopId,
  currency = "EUR",
  isConnectedUser = false,
  connectedUserTooltip,
}: LineItemRowProps) {
  const navigate = useNavigate();
  const addLineItem = useAddLineItem(orderId, shopId);

  const handleSelect = () => {
    addLineItem({
      lineItemId: lineItem.line_item_id,
      quantity: 1,
      reasonId: null,
      comment: null,
      imageUrls: [],
      resolution: null,
      resolutionOption: null,
      exchangeVariantId: null,
    });
    navigate(
      `/return-widget/${shopId}/order/${orderId}/line-item/${lineItem.line_item_id}/reason`,
    );
  };

  const options = formatVariantOptions(lineItem);

  const priceCents = parseLineItemPriceInCents(lineItem.price);
  const price = priceCents != null ? formatPrice(priceCents, currency) : null;
  const subtitle = [options, price].filter(Boolean).join(" • ") || null;

  return (
    <Article onClick={handleSelect} className="cursor-pointer">
      <ArticleImage
        src={lineItem.image}
        layoutId={`img-${lineItem.line_item_id}`}
      />
      <ArticleTitle>{lineItem.title}</ArticleTitle>
      {subtitle && <ArticleSubtitle>{subtitle}</ArticleSubtitle>}
      {isConnectedUser && lineItem.sku && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{lineItem.sku}</span>
          <Tooltip>
            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
      <ArticleAction>
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleSelect();
          }}
        >
          Retourner
          <ChevronRight />
        </Button>
      </ArticleAction>
    </Article>
  );
}
